import { createFileRoute } from '@tanstack/react-router';

import { useSession } from '@platform/api-client/react';
import { Alert, Card, Spinner } from '@platform/ui';

import { useTenantDetail } from '../../../features/tenants/use-tenant';
import { UsersTab } from '../../../features/tenants/users-tab';
import { useStartImpersonation } from '../../../lib/impersonation';
import { PLATFORM_PERMISSIONS, usePermissions } from '../../../lib/session/permissions';

export const Route = createFileRoute('/tenants/$tenantId/users')({
  component: TenantUsersPage,
});

/**
 * `/tenants/$id/users` (FR-SUPER-03, FR-SUPER-06, M2.8).
 *
 * "View as" is real from M2.8 on: the reason dialog collects the mandatory reason, and
 * `POST /api/v1/platform/impersonation` writes the `impersonation_sessions` row and the
 * audit row *before* it issues the session. That endpoint is step-up gated, so the call
 * goes through `useStartImpersonation`, which opens the step-up dialog and retries the
 * whole request - reason included - once a second factor verifies.
 *
 * The button is hidden without `platform.impersonate`. That is display only; the server
 * refuses the call either way (rules §17.2).
 *
 * ## One read, because one endpoint now owns `status`
 *
 * "View as" is offered for an account that can actually sign in, so it reads `status`.
 * Two endpoints used to answer that field and they did not agree: `GET /platform/users`
 * derived it from whether a credential, a passkey or an accepted invitation exists
 * (API.md §2.13), while `GET /platform/tenants/:id` read `user.last_login_at`, a column
 * nothing writes - so every staff account said `invited` and "View as" was greyed out
 * for people signing in daily. This route worked around it by also fetching
 * `/platform/users?tenantId=…` and merging the two derived fields onto the tenant
 * detail's rows.
 *
 * `TenantsService.#staffUsers` shares `accountSignalsFor` with `/platform/users` as of
 * `4029eb9`, so the tenant detail is the single source for this tab and the merge - and
 * its second request on every tab open - is gone (`SUPER_ADMIN_UI.md` item 41).
 */
function TenantUsersPage() {
  const { tenantId } = Route.useParams();
  const tenant = useTenantDetail(tenantId);
  const session = useSession();
  const { can } = usePermissions();
  const impersonation = useStartImpersonation();

  if (tenant.isPending) {
    return (
      <Card className="p-6">
        <Spinner label="Loading staff" />
      </Card>
    );
  }
  if (tenant.isError) {
    return <Alert tone="error">That tenant could not be loaded.</Alert>;
  }

  const summary = tenant.data.tenant;
  const impersonating = session.data?.impersonation ?? null;

  return (
    <UsersTab
      tenantName={summary.name}
      users={tenant.data.users}
      impersonatingUserId={impersonating?.targetUserId ?? null}
      readOnly={summary.status === 'archived' || !can(PLATFORM_PERMISSIONS.impersonate)}
      pending={impersonation.pending}
      onViewAs={(user, reason) => {
        void impersonation.start({ userId: user.id, tenantId, reason });
      }}
    />
  );
}
